import * as React from "react";
import {
  AnchorNavigation,
  Button,
  CopyToClipboard,
  ExpandableSection,
  Grid,
  Header,
  KeyValuePairs,
  KeyValuePairsProps,
  Link,
  Popover,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  SplitPanel,
} from "@amzn/awsui-components-console";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { InitDomain } from "src/context/domain/domainContext";
import { AssociatedPlans } from "./associated-plans";
import { UseCaseTables } from "./use-case-tables";
import { AccountingRegistrationModal } from "./accounting-registration-modal";
import { Ard } from "@amzn/fuse-accounting-central-datastore-client";
import {
  getLocalTime,
  getUserAlias,
  MARKDOWN_PAGE_GRID_DEFINITION,
  ANCHOR_SCROLL_MARGIN_TOP,
  SECTION_IDS,
  getUseCaseSectionId,
  SCROLL_INTO_VIEW_OPTIONS,
} from "./split-panel.helper";

interface JemGenSplitPanelProps {
  setToolsOpen: (open: boolean) => void;
}

export function JemGenSplitPanel({ setToolsOpen }: JemGenSplitPanelProps) {
  const {
    ardRegionMap,
    selectedArd,
    selectedArdDetailed,
    setToolsContentSelection,
    splitPanelSize,
    setSplitPanelSize,
    registrationsLoadingState,
    availableRegistrationId,
    fetchArdData,
  } = useJemGen();

  const { authService } = React.useContext(InitDomain);
  const canEdit = authService.ldap.includes("bart-admin");

  // Registration modal state (supports both view and create modes)
  const [showRegistrationModal, setShowRegistrationModal] =
    React.useState(false);
  const [registrationModalMode, setRegistrationModalMode] = React.useState<
    "view" | "create"
  >("view");

  const [displayedArd, setDisplayedArd] = React.useState<Ard | null>(null);
  const [revisionOptions, setRevisionOptions] = React.useState<
    SelectProps.Option[]
  >([]);
  const [selectedRevision, setSelectedRevision] =
    React.useState<SelectProps.Option | null>(null);

  // State for controlling expanded sections
  const [expandedSections, setExpandedSections] = React.useState<{
    details: boolean;
    fusePlans: boolean;
    useCases: boolean;
  }>({
    details: true,
    fusePlans: false,
    useCases: false,
  });

  const hasExistingRegistration = !!availableRegistrationId;

  const handleViewRegistrationClick = React.useCallback(() => {
    setRegistrationModalMode("view");
    setShowRegistrationModal(true);
  }, []);

  const handleCreateRegistrationClick = React.useCallback(() => {
    setRegistrationModalMode("create");
    setShowRegistrationModal(true);
  }, []);

  const handleRegistrationModalDismiss = React.useCallback(() => {
    setShowRegistrationModal(false);
  }, []);

  const handleCreateRegistrationSuccess = React.useCallback(() => {
    console.log("Registration created successfully from split panel");
    if (selectedArdDetailed?.ard?.id) {
      fetchArdData(selectedArdDetailed.ard.id);
    }
  }, [selectedArdDetailed?.ard?.id, fetchArdData]);

  // State for active anchor href (only changes on click)
  const [activeHref, setActiveHref] = React.useState<string>(() => {
    // Initialize from URL hash if present
    return window.location.hash || `#${SECTION_IDS.DETAILS}`;
  });

  // Calculate dynamic navigation height based on split panel size
  // Account for sticky top offset (80px), and padding (~40px)
  const navMaxHeight = React.useMemo(
    () => `${splitPanelSize - 120}px`,
    [splitPanelSize],
  );

  // Shared function to navigate to a section
  const navigateToSection = React.useCallback(
    (href: string, scrollDelay: number) => {
      const targetId = href.substring(1); // Remove the '#'

      // Auto-expand sections based on target
      if (href === `#${SECTION_IDS.DETAILS}`) {
        setExpandedSections((prev) => ({ ...prev, details: true }));
      } else if (href === `#${SECTION_IDS.FUSE_PLANS}`) {
        setExpandedSections((prev) => ({ ...prev, fusePlans: true }));
      } else if (
        href === `#${SECTION_IDS.USE_CASES}` ||
        href.startsWith("#use-case-")
      ) {
        setExpandedSections((prev) => ({ ...prev, useCases: true }));
      }

      // Scroll to target element after a delay to allow sections to expand
      setTimeout(() => {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
        }
      }, scrollDelay);
    },
    [],
  );

  // Scroll to section on initial load if URL has hash or displayedArd changes
  // Wait for registrations to finish loading before scrolling to avoid layout shifts
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash && displayedArd && registrationsLoadingState === "loaded") {
      navigateToSection(hash, 100);
    }
  }, [displayedArd, registrationsLoadingState, navigateToSection]);

  // Handle anchor clicks to auto-expand sections
  const handleAnchorFollow = React.useCallback(
    (event: CustomEvent) => {
      event.preventDefault(); // Prevent default page scroll

      const href = event.detail.href;
      setActiveHref(href);

      // Update URL hash without triggering page scroll
      window.history.pushState(null, "", href);

      // Navigate to the section
      navigateToSection(href, 100);
    },
    [navigateToSection],
  );

  React.useEffect(() => {
    if (
      !selectedArdDetailed ||
      !selectedArdDetailed.revisions ||
      !selectedArdDetailed.revisions.length
    ) {
      setSelectedRevision(null);
      return;
    }

    const totalRevisions = selectedArdDetailed.revisions.length;
    const revisions = selectedArdDetailed.revisions;
    const newRevisionOptions = Array.from(
      new Array(totalRevisions).fill(0),
      (_, index) => index,
    )
      .map((i) => {
        const suffix = i === totalRevisions - 1 ? " (Latest)" : "";
        return {
          label: `Revision ${i}` + suffix,
          value: `${i}`,
          description: revisions[i].changeAudit?.description,
          tags: [
            getUserAlias(revisions[i].changeAudit?.changedBy) ||
              revisions[i].changeAudit?.changedBy,
          ],
          labelTag: getLocalTime(revisions[i].changeAudit?.changedAt),
        } as SelectProps.Option;
      })
      .reverse();
    setRevisionOptions(newRevisionOptions);
    setSelectedRevision(newRevisionOptions[0]);
  }, [selectedArdDetailed]);

  React.useEffect(() => {
    if (
      !selectedArdDetailed ||
      !selectedArdDetailed.revisions ||
      !selectedRevision ||
      Number(selectedRevision.value) >= selectedArdDetailed?.revisions.length
    ) {
      setDisplayedArd(null);
      return;
    }

    setDisplayedArd(
      selectedArdDetailed.revisions[Number(selectedRevision.value)].ard || null,
    );
  }, [selectedRevision]);

  // Transform ARD data into KeyValuePairs format
  const getArdKeyValueItems = () => {
    if (!displayedArd || !selectedArdDetailed?.ard) {
      return [];
    }

    return [
      {
        label: "ARD ID",
        value: (
          <CopyToClipboard
            copyButtonAriaLabel="Copy ARD ID"
            copyErrorText="ARD ID failed to copy"
            copySuccessText="ARD ID copied"
            textToCopy={displayedArd.id || ""}
            variant="inline"
          />
        ),
      },
      {
        label: "Billing Mode",
        value: displayedArd.billingMode || "N/A",
      },
      {
        label: "Product",
        value: displayedArd.product || "N/A",
      },
      {
        label: "Term",
        value: displayedArd.term || "N/A",
      },
      {
        label: "Template",
        value: displayedArd.template || "N/A",
      },
      {
        label: "Business Country Code",
        value: displayedArd.businessCountryCode || "N/A",
      },
      {
        label: "Buying Customer Country Code",
        value: displayedArd.buyingCustomerCountryCode || "N/A",
      },
      {
        label: "Functional Currency",
        value: displayedArd.functionalCurrency || "N/A",
      },
      {
        label: "Local Currency",
        value: displayedArd.localCurrency || "N/A",
      },
      {
        label: "Domain",
        value: ardRegionMap.get(displayedArd.id || "") || "N/A",
      },
      {
        label: "Fuse Plans",
        value: selectedArdDetailed.ard.associatedPlans ? (
          <Link
            variant="primary"
            href={`#${SECTION_IDS.FUSE_PLANS}`}
            onFollow={handleAnchorFollow}
          >
            {selectedArdDetailed.ard.associatedPlans.length.toString() +
              " Fuse plan(s)"}
          </Link>
        ) : (
          "N/A"
        ),
      },
      {
        label: "Use Cases",
        value: displayedArd.usecases ? (
          <Link
            variant="primary"
            href={`#${SECTION_IDS.USE_CASES}`}
            onFollow={handleAnchorFollow}
          >
            {displayedArd.usecases.length.toString() + " use case(s)"}
          </Link>
        ) : (
          "N/A"
        ),
      },
    ];
  };

  const changeAuditKeyValueItems = React.useMemo(() => {
    // Early return for loading state
    if (
      !selectedArdDetailed?.revisions ||
      !selectedRevision ||
      Number(selectedRevision.value) >= selectedArdDetailed?.revisions.length
    ) {
      return [
        { label: "Changed by", value: <Spinner /> },
        { label: "Changed at", value: <Spinner /> },
        { label: "Change description", value: <Spinner /> },
      ] as ReadonlyArray<KeyValuePairsProps.Item>;
    }

    // Extract the change audit data once
    const revisionIndex = Number(selectedRevision.value);
    const changeAudit =
      selectedArdDetailed.revisions[revisionIndex]?.changeAudit;

    const userAlias = getUserAlias(changeAudit?.changedBy);

    return [
      {
        label: "Changed by",
        value: userAlias ? (
          <Popover
            renderWithPortal
            header="Principal"
            triggerType="custom"
            content={changeAudit?.changedBy}
          >
            <Link
              external
              href={`https://phonetool.amazon.com/users/${userAlias}`}
            >
              {userAlias}
            </Link>
            <span style={{ marginLeft: 15 }}>
              <Button variant="inline-icon" iconName="status-info" />
            </span>
          </Popover>
        ) : (
          changeAudit?.changedBy || "N/A"
        ),
      },
      {
        label: "Changed at",
        value: getLocalTime(changeAudit?.changedAt) || "N/A",
      },
      {
        label: "Change description",
        value: changeAudit?.description || "N/A",
      },
    ] as ReadonlyArray<KeyValuePairsProps.Item>;
  }, [selectedArdDetailed, selectedRevision]);

  return (
    <SplitPanel
      header={"ARD Details"}
      headerActions={
        (selectedArdDetailed && revisionOptions.length && selectedRevision && (
          <SpaceBetween size="xs" direction="horizontal">
            <div style={{ maxWidth: "500px" }}>
              <Select
                triggerVariant="label"
                disabled={!selectedArdDetailed}
                selectedOption={selectedRevision}
                onChange={({ detail }) =>
                  setSelectedRevision(detail.selectedOption)
                }
                options={revisionOptions}
                ariaLabel="Select ARD revision"
              />
            </div>
            {hasExistingRegistration ? (
              <Button
                iconName="file-open"
                variant="normal"
                onClick={handleViewRegistrationClick}
              >
                View Registration
              </Button>
            ) : (
              canEdit && (
                <Button
                  iconName="add-plus"
                  variant="normal"
                  onClick={handleCreateRegistrationClick}
                  disabled={registrationsLoadingState !== "loaded"}
                  disabledReason="Loading registration data…"
                >
                  Create Registration
                </Button>
              )
            )}
            <Button
              iconName="command-prompt"
              variant="normal"
              ariaLabel="Open Aided Flare Authoring guide"
              onClick={() => {
                setToolsContentSelection("AidedFlareAuthoring");
                setToolsOpen(true);
              }}
            >
              Flare Authoring
            </Button>
            <Button
              iconName="view-full"
              variant="icon"
              onClick={() => {
                setSplitPanelSize(window.innerHeight * 0.8);
              }}
            />
            <Button
              iconName="view-horizontal"
              variant="icon"
              onClick={() => {
                setSplitPanelSize(window.innerHeight * 0.4);
              }}
            />
          </SpaceBetween>
        )) ||
        (displayedArd && <Spinner size="big" />)
      }
      i18nStrings={{
        preferencesTitle: "Split Panel Preferences",
        preferencesPositionSide: "Side",
        preferencesPositionBottom: "Bottom",
        preferencesConfirm: "Confirm",
        preferencesCancel: "Cancel",
      }}
    >
      <Grid gridDefinition={MARKDOWN_PAGE_GRID_DEFINITION}>
        <div style={{ marginTop: "8px", height: "100%" }}>
          <div
            style={{
              position: "sticky",
              top: "105px",
              zIndex: 1000,
              maxHeight: navMaxHeight,
              overflowY: "auto",
              wordBreak: "break-all",
            }}
          >
            <Header variant="h3">
              <span id="navigation-header">On this page</span>
            </Header>
            <AnchorNavigation
              anchors={[
                {
                  text: "Details",
                  href: `#${SECTION_IDS.DETAILS}`,
                  level: 1,
                },
                {
                  text: "Fuse Plans",
                  href: `#${SECTION_IDS.FUSE_PLANS}`,
                  level: 1,
                },
                {
                  text: "Use cases",
                  href: `#${SECTION_IDS.USE_CASES}`,
                  level: 1,
                },
                // Add subsections for each use case
                ...(displayedArd?.usecases || []).map((useCase, index) => {
                  const useCaseName =
                    useCase.name === "OTHER" && useCase.altName
                      ? `${useCase.name} (${useCase.altName})`
                      : useCase.name || "Unknown";
                  return {
                    text: useCaseName,
                    href: `#${getUseCaseSectionId(useCase.name, index)}`,
                    level: 2,
                  };
                }),
              ]}
              ariaLabelledby="navigation-header"
              onFollow={handleAnchorFollow}
              activeHref={activeHref}
            />
          </div>
        </div>
        <SpaceBetween size="xl">
          <div
            id={SECTION_IDS.DETAILS}
            style={{ scrollMarginTop: `${ANCHOR_SCROLL_MARGIN_TOP}px` }}
          >
            <ExpandableSection
              variant="container"
              expanded={expandedSections.details}
              onChange={({ detail }) =>
                setExpandedSections((prev) => ({
                  ...prev,
                  details: detail.expanded,
                }))
              }
              headerText="Details"
            >
              {!displayedArd ? (
                !selectedArd ? (
                  "Select an ARD to see its details."
                ) : (
                  <Spinner />
                )
              ) : (
                <SpaceBetween size="xl">
                  <KeyValuePairs columns={4} items={getArdKeyValueItems()} />
                  <KeyValuePairs
                    columns={2}
                    items={changeAuditKeyValueItems.slice(0, 2)}
                  />
                  <KeyValuePairs
                    columns={1}
                    items={changeAuditKeyValueItems.slice(2, 3)}
                  />
                </SpaceBetween>
              )}
            </ExpandableSection>
          </div>
          <div
            id={SECTION_IDS.FUSE_PLANS}
            style={{ scrollMarginTop: `${ANCHOR_SCROLL_MARGIN_TOP}px` }}
          >
            <ExpandableSection
              variant="container"
              expanded={expandedSections.fusePlans}
              onChange={({ detail }) =>
                setExpandedSections((prev) => ({
                  ...prev,
                  fusePlans: detail.expanded,
                }))
              }
              headerText="Fuse Plans"
              headerCounter={
                !selectedArd
                  ? undefined
                  : `(${selectedArd?.associatedPlans?.length || 0})`
              }
            >
              <AssociatedPlans />
            </ExpandableSection>
          </div>
          <div
            id={SECTION_IDS.USE_CASES}
            style={{ scrollMarginTop: `${ANCHOR_SCROLL_MARGIN_TOP}px` }}
          >
            <ExpandableSection
              variant="container"
              expanded={expandedSections.useCases}
              onChange={({ detail }) =>
                setExpandedSections((prev) => ({
                  ...prev,
                  useCases: detail.expanded,
                }))
              }
              headerText="Use cases"
              headerCounter={
                !(selectedArd && displayedArd)
                  ? undefined
                  : `(${displayedArd.usecases?.length || 0})`
              }
            >
              <UseCaseTables displayedArd={displayedArd} />
            </ExpandableSection>
          </div>
          {/* Blank space to allow scrolling small sections into view */}
          <div style={{ height: navMaxHeight }} />
        </SpaceBetween>
      </Grid>

      {selectedArdDetailed?.ard && (
        <AccountingRegistrationModal
          visible={showRegistrationModal}
          accountingRegistrationId={
            registrationModalMode === "view" ? availableRegistrationId : null
          }
          onDismiss={handleRegistrationModalDismiss}
          mode={registrationModalMode}
          ardForCreate={
            registrationModalMode === "create"
              ? selectedArdDetailed.ard
              : undefined
          }
          allPlans={
            registrationModalMode === "create"
              ? selectedArdDetailed.ard.associatedPlans
              : undefined
          }
          onCreateSuccess={handleCreateRegistrationSuccess}
        />
      )}
    </SplitPanel>
  );
}
